import React, { useState } from 'react';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import { Link } from 'react-router-dom';
import LoadingIndicator from 'components/LoadingIndicator';
import { CV_WEB_SAPPHIRES_NEW_UX_ENABLED } from 'utils/constants';
import { formatNumber } from 'utils/formatting';
import { QUERY_GET_REMOTE_CONFIG_ENTRY } from 'utils/hook/query/useRemoteConfigEntry';
import { userShape } from 'utils/shapes';
import ProfileVaultNew from '../ProfileVaultNew';
import CurrencyCard from './CurrencyCard';
import SapphireInfoModal from './SapphireInfoModal';
import { CmpCurrencyPanel, PlsCurrencyPanel } from './casinoCurrencyPanel';
import WStoreWinIcon from './assets/WStoreWin.png';
import './ProfileVault.css';

const WSTORE_LINK_TO = {
  pathname: '/wstore',
  state: { initiator: 'profile-vault-wstore-promo' }
};

const ProfileVault = ({
  userData,
  assetsData,
  statusCmpData,
  statusPlsData,
  closeProfileAndRewardsModal,
  newSapphiresUXRemoteConfigData,
  userEarningPotentialData,
  patronRewards,
  plsRewards
}) => {
  const [showInfoModal, setShowInfoModal] = useState(false);

  const statusData = statusCmpData || statusPlsData;

  const isLoading =
    (userEarningPotentialData && userEarningPotentialData.loading) ||
    (newSapphiresUXRemoteConfigData &&
      newSapphiresUXRemoteConfigData.loading) ||
    (userData && userData.loading) ||
    (statusData && statusData.loading) ||
    (assetsData && assetsData.loading);
  const isError =
    (userEarningPotentialData && userEarningPotentialData.error) ||
    (newSapphiresUXRemoteConfigData && newSapphiresUXRemoteConfigData.error) ||
    (userData && userData.error) ||
    (statusData && statusData.error) ||
    (assetsData && assetsData.error);

  if (isLoading) {
    return <LoadingIndicator />;
  } else if (isError) {
    return (
      <div className="alert alert-danger">
        Failed to retrieve player currencies. If this continues, please contact
        support.
      </div>
    );
  }

  const newSapphiresUXEnabled =
    newSapphiresUXRemoteConfigData?.remoteConfigEntry?.value;

  const patronAssets =
    assetsData && assetsData.patronAssets
      ? assetsData.patronAssets
      : assetsData && assetsData.lookupPatronAssets;

  const casinoLocation = userData.user.preferredLocation;
  const CasinoCurrencyPanelComponent = statusCmpData
    ? CmpCurrencyPanel
    : PlsCurrencyPanel;

  const renderOldUI = () => (
    <>
      <SapphireInfoModal
        show={showInfoModal}
        onClose={() => setShowInfoModal(false)}
      />
      <div className="profile-vault">
        <div className="vault-currency-container">
          <div className="online-currency-panel">
            <div className="title">ONLINE</div>
            {patronAssets && (
              <div className="currency-cards">
                <CurrencyCard
                  currencyAmount={patronAssets.wscore}
                  currencyLabel="WScore"
                  cardType="wscore"
                  format={formatNumber}
                />
                <CurrencyCard
                  currencyAmount={patronAssets.gameBalance}
                  currencyLabel="Credits"
                  cardType="credits"
                  format={formatNumber}
                />
                <CurrencyCard
                  currencyAmount={patronAssets.tickets}
                  currencyLabel="Online Entries"
                  cardType="online-entries"
                  format={formatNumber}
                />
                <CurrencyCard
                  currencyAmount={patronAssets.sapphires}
                  currencyLabel="Sapphires"
                  cardType="sapphires"
                  format={formatNumber}
                />
              </div>
            )}
          </div>
          <div className="casino-currency-panel">
            <div className="casino-loc">{casinoLocation}</div>
            {statusData && (
              <CasinoCurrencyPanelComponent statusData={statusData} />
            )}
          </div>
        </div>
        <div className="vault-footer">
          <div className="wstore-promo">
            <div className="wstore-promo-text-container">
              <img src={WStoreWinIcon} alt="" />
              <div className="wstore-promo-text">
                Explore the WStore to discover exclusive Wind Creek offers!
              </div>
            </div>
            <Link
              className="button-color-blue vault-wstore-btn"
              to={WSTORE_LINK_TO}
              onClick={closeProfileAndRewardsModal}
            >
              SHOP WSTORE
            </Link>
          </div>
        </div>
      </div>
    </>
  );

  const renderNewUI = () => (
    <ProfileVaultNew
      userData={userData}
      assetsData={assetsData}
      statusCmpData={statusCmpData}
      statusPlsData={statusPlsData}
      patronRewards={patronRewards}
      plsRewards={plsRewards}
      userEarningPotentialData={userEarningPotentialData}
    />
  );

  return newSapphiresUXEnabled ? renderNewUI() : renderOldUI();
};

ProfileVault.propTypes = {
  userData: PropTypes.shape({
    user: userShape
  }),
  assetsData: PropTypes.shape({
    patronAssets: PropTypes.shape({
      gameBalance: PropTypes.number,
      nuggets: PropTypes.number,
      rubies: PropTypes.number,
      sapphires: PropTypes.number,
      tickets: PropTypes.number,
      wchips: PropTypes.number,
      wscore: PropTypes.number
    })
  }),
  statusCmpData: PropTypes.shape({
    statusv2: PropTypes.shape({
      cmpPlayerStatus: PropTypes.shape({
        freePlay: PropTypes.number,
        basePoints: PropTypes.number,
        drawings: PropTypes.arrayOf(
          PropTypes.shape({
            amount: PropTypes.number
          })
        )
      })
    })
  }),
  statusPlsData: PropTypes.shape({
    statusv2: PropTypes.shape({
      plsPlayerStatus: PropTypes.shape({
        freePlay: PropTypes.number,
        pointsBalance: PropTypes.number
      })
    })
  }),
  closeProfileAndRewardsModal: PropTypes.func,
  newSapphiresUXRemoteConfigData: PropTypes.shape({
    remoteConfigEntry: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.bool
    })
  }),
  userEarningPotentialData: PropTypes.shape({
    getUserEarningPotential: PropTypes.shape({
      gemEarningPotential: PropTypes.number,
      remainingGemEarningPotential: PropTypes.number
    })
  }),
  patronRewards: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string,
      description: PropTypes.string
    })
  ),
  plsRewards: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string,
      remainingValue: PropTypes.string
    })
  )
};

const REMOTE_CONFIG_EVENT = gql`
  subscription remoteConfigEvent {
    remoteConfigEvent {
      action
      entries {
        key
        value
      }
    }
  }
`;

const withSapphiresNewUX = graphql(QUERY_GET_REMOTE_CONFIG_ENTRY, {
  name: 'newSapphiresUXRemoteConfigData',
  options: {
    variables: {
      key: CV_WEB_SAPPHIRES_NEW_UX_ENABLED
    },
    fetchPolicy: 'cache-and-network',
    context: {
      //TODO: remove once LEGACY FEED and casinoverse file is removed
      headers: {
        'X-WCC-Is-Casinoverse': true
      }
    }
  },
  props: ({ ownProps, newSapphiresUXRemoteConfigData }) => {
    return {
      newSapphiresUXRemoteConfigData,
      subscribeToNewData: () => {
        return newSapphiresUXRemoteConfigData.subscribeToMore({
          document: REMOTE_CONFIG_EVENT,
          updateQuery: (prev, { subscriptionData }) => {
            if (subscriptionData?.data?.action === 'Update') {
              subscriptionData.data.entries.forEach(({ key, value }) => {
                if (key === CV_WEB_SAPPHIRES_NEW_UX_ENABLED) {
                  prev.remoteConfigEntry.value = value;
                }
              });
            }
            return prev;
          }
        });
      }
    };
  }
});

const QUERY_GET_USER_EARNING_POTENTIAL = gql`
  query GetUserEarningPotential {
    getUserEarningPotential {
      remainingGemEarningPotential
      gemEarningPotential
    }
  }
`;

const withUserEarningPotential = graphql(QUERY_GET_USER_EARNING_POTENTIAL, {
  name: 'userEarningPotentialData',
  options: {
    fetchPolicy: 'cache-and-network',
    context: {
      //TODO: remove once LEGACY FEED and casinoverse file is removed
      headers: {
        'X-WCC-Is-Casinoverse': true
      }
    }
  }
});

export default withUserEarningPotential(withSapphiresNewUX(ProfileVault));
